"use client";

import dynamic from "next/dynamic";
import { useState, type FocusEvent, type PointerEvent } from "react";

import BirthToolFormShell from "@/components/tool-shell/BirthToolFormShell";

import type { DashaLordId } from "./running-dasha";
import {
  RUNNING_DASHA_FIELD_IDS,
  RUNNING_DASHA_FORM,
  RUNNING_DASHA_FRAME,
} from "./running-dasha-form";

// The /mahadasha/<planet>/ running-dasha boundary (the #2460 pattern): the
// server page imports only this module, and it owns the one dynamic() to the
// island. The form, place search, kundali client, verdict and analytics load
// as an async chunk; the first load gains only this file and the shell.
//
// Activation is element-scoped and local: no window listener and no timer
// (this branch's useDeferredToolActivation has both), so a reader scrolling
// the article never downloads the island and the deploy's body pointerdown
// smoke loads nothing; a press, focus or submit on the shell does.
//
// The slot reserves the tallest state and every state fills it, so neither
// shell -> form nor a slow engine reply -> verdict (or error) moves the page
// below. Natural heights measured 2026-10-06 (next dev + Playwright, /kundali
// mocked with a 2 s reply): at 320 px wide the shell is 420, the form with a
// place hint 448, an error 516, engine verdicts up to 574 and a synthetic worst
// case (birth mahadasha still running, seven lords listed, every date in
// "Sept") 595; at 360 px verdicts reach 574; from 640 px up the tallest
// verdict is 473. Hence 600 px on phones and 500 px from the sm breakpoint; the
// content below did not move in any state. translate="no" covers the shell,
// form and verdict, which re-render on "Check another birth date"
// (tests/translate-safe-islands.test.ts).
const SHELL_PROPS = { ...RUNNING_DASHA_FORM, className: RUNNING_DASHA_FRAME } as const;

const RunningDashaCheck = dynamic(() => import("./RunningDashaCheck"), {
  ssr: false,
  loading: () => <BirthToolFormShell {...SHELL_PROPS} />,
});

/** BirthToolFormShell input names -> the real form's input ids. */
const SHELL_FIELD_IDS: Readonly<Record<string, string>> = {
  dob: RUNNING_DASHA_FIELD_IDS.date,
  tob: RUNNING_DASHA_FIELD_IDS.time,
  place: RUNNING_DASHA_FIELD_IDS.place,
};

function formFieldId(target: EventTarget | null): string {
  const name = target instanceof Element ? target.getAttribute("name") : null;
  return (name && SHELL_FIELD_IDS[name]) || RUNNING_DASHA_FIELD_IDS.date;
}

export default function RunningDashaCheckLazy({ pageLord }: { pageLord: DashaLordId }) {
  const [active, setActive] = useState(false);
  const [focusFieldId, setFocusFieldId] = useState<string | null>(null);

  // The shell's own onActivate cannot tell a keyboard focus from a touch, so
  // the slot reads the intent in the capture phase: focus follows the visitor
  // into the real form after a keyboard focus, a mouse press or a submit, and
  // never after a touch, which may be the start of a scroll.
  const shellIntent = active
    ? {}
    : {
        onPointerDownCapture: (event: PointerEvent<HTMLDivElement>) =>
          setFocusFieldId(event.pointerType === "mouse" ? formFieldId(event.target) : null),
        onFocusCapture: (event: FocusEvent<HTMLDivElement>) =>
          setFocusFieldId(formFieldId(event.target)),
        onSubmitCapture: () => setFocusFieldId(RUNNING_DASHA_FIELD_IDS.date),
      };

  return (
    <div
      translate="no"
      data-running-dasha-slot=""
      data-page-lord={pageLord}
      className="flex min-h-[600px] flex-col sm:min-h-[500px]"
      {...shellIntent}
    >
      {active ? (
        <RunningDashaCheck pageLord={pageLord} focusFieldId={focusFieldId} />
      ) : (
        <BirthToolFormShell {...SHELL_PROPS} onActivate={() => setActive(true)} />
      )}
    </div>
  );
}
